import React, { useEffect, useMemo, useRef, useState } from 'react';
import { authenticatedApiCall } from '../../utils/apiClient';
import { OPERATION_CODES } from '../../data/codingStructureData';
import {
  DEMO_CREWS,
  DEMO_EMPLOYEE,
  DEMO_EMPLOYEES,
  demoEntriesForWeek,
} from './demoModuleData';
import { saveErrorMessage } from './saveQueue';
import { HeaderTools, MobileSelect } from './screens';
import {
  DAY_LABELS,
  allowanceTotal,
  appendNumeric,
  CREW_REVIEW_HEADS,
  applyAllowances,
  applyJobAndCode,
  applyRegularHours,
  applyRepeatedLine,
  buildCrewEntries,
  buildWeekEntries,
  catalogueLabourCodes,
  copyWeekJobCodes,
  createCrewRow,
  createWeeklyLine,
  crewReviewTable,
  crewPeople,
  currentDateString,
  dailyTotalsByEmployee,
  dayTotal,
  defaultCrewProject,
  emptyAllowances,
  emptyDayHours,
  filterEmployees,
  filterOpCodes,
  formatDayHoursLabel,
  formatJobCodeLabel,
  formatMoney,
  formatWeekRange,
  groupWeekEntries,
  hourValue,
  hoursForDate,
  inferDisciplineCode,
  isSameEmployee,
  lastFilledWeeklyLine,
  lineHasJobAndCode,
  linesWeekTotal,
  dailyPadChain,
  mapEmployee,
  nextPadFocus,
  normalizeList,
  normalizeOpCodes,
  overDailyLimit,
  padKey,
  readLastWeeklyLine,
  rememberLastWeeklyLine,
  setLineDayHours,
  shiftWeek,
  sumAllowancesByDate,
  weekCellLabel,
  weekForDate,
  weekReviewTable,
  weekTotal,
  weeklyPadChain,
} from './timeTrackerHelpers';

const CATALOGUE_CODES = catalogueLabourCodes(OPERATION_CODES);

const Back = ({ onClick }) => (
  <button type="button" className="lgl-m-back" onClick={onClick} aria-label="Back to menus">←</button>
);

const CodePicker = ({ value, onSelect, codes = CATALOGUE_CODES }) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const boxRef = useRef(null);

  useEffect(() => {
    if (!open) setQuery('');
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const close = (event) => {
      if (boxRef.current && !boxRef.current.contains(event.target)) setOpen(false);
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [open]);

  const matches = useMemo(() => filterOpCodes(codes, query, 12), [codes, query]);
  const label = value ? `${value.code} · ${value.description}` : 'Select operation code';

  return (
    <section className="lgl-m-field" ref={boxRef}>
      <label>Operation code</label>
      {open ? (
        <>
          <input
            type="search"
            placeholder="Search code or description"
            value={query}
            autoFocus
            onChange={(event) => setQuery(event.target.value)}
          />
          <div className="lgl-m-typeahead" role="listbox" aria-label="Operation codes">
            {matches.length ? matches.map((code) => (
              <button
                key={code.code}
                type="button"
                role="option"
                aria-selected={value?.code === code.code}
                onClick={() => {
                  onSelect(code);
                  setOpen(false);
                }}
              >
                <strong>{code.code}</strong>
                <span>{code.description}</span>
              </button>
            )) : (
              <p className="lgl-m-empty">No codes match that search.</p>
            )}
          </div>
        </>
      ) : (
        <div className="lgl-m-codepick">
          <button
            type="button"
            className={`lgl-m-codepick-btn${value ? ' has-value' : ''}`}
            aria-haspopup="listbox"
            aria-expanded={false}
            onClick={() => setOpen(true)}
          >
            <span>{label}</span>
          </button>
          {value ? (
            <button
              type="button"
              className="lgl-m-codepick-clear"
              aria-label="Clear operation code"
              onClick={() => onSelect(null)}
            >
              ×
            </button>
          ) : null}
        </div>
      )}
    </section>
  );
};

const EmployeePicker = ({
  value,
  selfEmployee,
  demo,
  instance,
  accounts,
  onSelect,
}) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [matches, setMatches] = useState([]);
  const [searching, setSearching] = useState(false);
  const boxRef = useRef(null);
  const ownWeek = isSameEmployee(value, selfEmployee);

  useEffect(() => {
    if (!open) setQuery('');
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const close = (event) => {
      if (boxRef.current && !boxRef.current.contains(event.target)) setOpen(false);
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    if (demo) {
      setMatches(filterEmployees(DEMO_EMPLOYEES, query));
      setSearching(false);
      return undefined;
    }
    if (!query.trim()) {
      setMatches(selfEmployee ? [selfEmployee] : []);
      setSearching(false);
      return undefined;
    }
    let active = true;
    const timer = window.setTimeout(async () => {
      setSearching(true);
      try {
        const response = await authenticatedApiCall(
          instance,
          accounts,
          'GET',
          `/lt-employee/search?q=${encodeURIComponent(query.trim())}&limit=20`
        );
        const rows = normalizeList(response.data?.data ?? response.data)
          .map(mapEmployee)
          .filter((person) => person.emp_code);
        if (active) setMatches(rows);
      } catch {
        if (active) setMatches([]);
      } finally {
        if (active) setSearching(false);
      }
    }, 200);
    return () => {
      active = false;
      window.clearTimeout(timer);
    };
  }, [accounts, demo, instance, open, query, selfEmployee]);

  const label = value?.emp_name
    ? `${value.emp_name} · ${value.emp_code}`
    : 'Select whose week';

  return (
    <section className="lgl-m-field lgl-m-whose" ref={boxRef}>
      <label>Whose week</label>
      {open ? (
        <>
          <input
            type="search"
            placeholder="Search name or employee code"
            value={query}
            autoFocus
            onChange={(event) => setQuery(event.target.value)}
          />
          <div className="lgl-m-typeahead lgl-m-people" role="listbox" aria-label="Employees">
            {searching ? <p className="lgl-m-empty">Searching…</p> : null}
            {!searching && matches.length ? matches.map((person) => (
              <button
                key={person.emp_code}
                type="button"
                role="option"
                aria-selected={isSameEmployee(value, person)}
                onClick={() => {
                  onSelect(person);
                  setOpen(false);
                }}
              >
                <strong>{person.emp_name || person.emp_code}</strong>
                <span>{person.emp_code}{isSameEmployee(person, selfEmployee) ? ' · You' : ''}</span>
              </button>
            )) : null}
            {!searching && !matches.length ? (
              <p className="lgl-m-empty">No employees match that search.</p>
            ) : null}
          </div>
        </>
      ) : (
        <div className="lgl-m-codepick">
          <button
            type="button"
            className={`lgl-m-codepick-btn${value ? ' has-value' : ''}`}
            aria-haspopup="listbox"
            aria-expanded={false}
            aria-label={ownWeek && value?.emp_name
              ? `Whose week: ${value.emp_name} (you)`
              : `Whose week: ${value?.emp_name || 'not selected'}`}
            onClick={() => setOpen(true)}
          >
            <span>
              {label}
              {ownWeek && value ? <span className="lgl-m-you">You</span> : null}
            </span>
          </button>
        </div>
      )}
    </section>
  );
};

const padFieldClass = (padFocus, focus) => (
  `lgl-m-padfield${padKey(padFocus) === padKey(focus) ? ' is-on' : ''}`
);

const PadNumberInput = ({
  id,
  label,
  value,
  onChange,
  padFocus,
  focus,
  onPadFocus,
}) => (
  <section className="lgl-m-field lgl-m-hours-field">
    <label htmlFor={id}>{label}</label>
    <input
      id={id}
      type="number"
      inputMode="none"
      readOnly
      className={padFieldClass(padFocus, focus)}
      value={value ?? ''}
      onChange={(event) => onChange(event.target.value)}
      onFocus={() => onPadFocus?.(focus)}
      onClick={() => onPadFocus?.(focus)}
    />
  </section>
);

const AllowanceInputs = ({ value = {}, onChange, idPrefix = 'allow', padFocus, onPadFocus, padFor }) => (
  <div className="lgl-m-hours-grid lgl-m-allow-grid">
    {[
      ['rnb', 'R&B'],
      ['meals', 'Meals'],
      ['travel_2', 'Travel'],
    ].map(([field, label]) => (
      <PadNumberInput
        key={field}
        id={`${idPrefix}-${field}`}
        label={label}
        value={value[field]}
        onChange={(next) => onChange(field, next)}
        padFocus={padFocus}
        focus={padFor(field)}
        onPadFocus={onPadFocus}
      />
    ))}
  </div>
);

const HourInputs = ({ hours, onChange, idPrefix = 'hours', onFillRegular, padFocus, onPadFocus, padFor }) => (
  <>
    <div className="lgl-m-hours-grid">
      {[
        ['reg', 'Regular'],
        ['t12', '1.5x'],
        ['dbl', 'Double'],
      ].map(([field, label]) => (
        <PadNumberInput
          key={field}
          id={`${idPrefix}-${field}`}
          label={label}
          value={hours[field]}
          onChange={(next) => onChange(field, next)}
          padFocus={padFocus}
          focus={padFor(field)}
          onPadFocus={onPadFocus}
        />
      ))}
    </div>
    {onFillRegular ? (
      <button type="button" className="lgl-m-chipbtn" onClick={onFillRegular}>
        8h regular
      </button>
    ) : null}
  </>
);

const NumberPad = ({ onKey, onNext, onDone }) => (
  <div className="lgl-m-numpad" role="group" aria-label="Number pad">
    {['1', '2', '3', '4', '5', '6', '7', '8', '9', '.', '0', 'back'].map((key) => (
      <button
        key={key}
        type="button"
        aria-label={key === 'back' ? 'Backspace' : key}
        onClick={() => onKey(key)}
      >
        {key === 'back' ? '⌫' : key}
      </button>
    ))}
    <div className="lgl-m-numpad-actions">
      <button type="button" onClick={onNext}>Next</button>
      <button type="button" onClick={onDone}>Done</button>
    </div>
  </div>
);

const reviewHead = (head, index) => {
  if (head && typeof head === 'object') {
    return { key: head.id || head.label || index, label: head.label, num: head.num };
  }
  return { key: String(head || index), label: head, num: null };
};

const ReviewTable = ({ dayHeads = [], rows = [], label = 'Item' }) => (
  <table className="lgl-m-review-table">
    <thead>
      <tr>
        <th scope="col">{label}</th>
        {dayHeads.map((head, index) => {
          const cell = reviewHead(head, index);
          return (
            <th key={cell.key} scope="col">
              <span>{cell.label}</span>
              {cell.num != null ? <em>{cell.num}</em> : null}
            </th>
          );
        })}
      </tr>
    </thead>
    <tbody>
      {rows.map((row) => (
        <tr key={row.id}>
          <th scope="row">{row.label}</th>
          {row.cells.map((cell, index) => (
            <td key={`${row.id}-${index}`} className={cell === '—' ? 'is-empty' : 'has-value'}>{cell}</td>
          ))}
        </tr>
      ))}
    </tbody>
  </table>
);

const JobSelect = ({ projects, value, onChange }) => (
  <MobileSelect
    id="time-job"
    label="Job"
    placeholder="Select a job"
    value={value}
    options={projects.map((project) => ({
      value: project.id,
      label: `${project.job_number} · ${project.job_name || 'Untitled job'}`,
    }))}
    onChange={onChange}
  />
);

const jobFields = (project, extra = {}) => ({
  project_id: project?.id || project?.project_id || '',
  job_number: project?.job_number || '',
  discipline_code: inferDisciplineCode(project),
  ...extra,
});

export const TimeTrackerScreen = ({
  accounts,
  demo,
  instance,
  projects = [],
  onBack,
}) => {
  const [mode, setMode] = useState(demo ? 'weekly' : 'daily');
  const [canUseWeekly, setCanUseWeekly] = useState(Boolean(demo));
  const [selfEmployee, setSelfEmployee] = useState(demo ? DEMO_EMPLOYEE : null);
  const [employee, setEmployee] = useState(demo ? DEMO_EMPLOYEE : null);
  const [weekAnchor, setWeekAnchor] = useState(currentDateString);
  const [weeklyLines, setWeeklyLines] = useState(() => [createWeeklyLine(projects[0] || null)]);
  const [dailyAllowances, setDailyAllowances] = useState({});
  const [openDay, setOpenDay] = useState(null);
  const [openAllowances, setOpenAllowances] = useState(null);
  const [crewShareAllowances, setCrewShareAllowances] = useState(() => emptyAllowances());
  const [padFocus, setPadFocus] = useState(null);
  const [crews, setCrews] = useState(demo ? DEMO_CREWS : []);
  const [crewId, setCrewId] = useState(demo ? DEMO_CREWS[0]?.id || '' : '');
  const [crewDate, setCrewDate] = useState(currentDateString);
  const [crewRows, setCrewRows] = useState(() => (
    demo ? crewPeople(DEMO_CREWS[0]).map((person) => createCrewRow(person, defaultCrewProject(DEMO_CREWS[0]) || projects[0])) : []
  ));
  const [codeCache, setCodeCache] = useState({});
  const [loading, setLoading] = useState(!demo);
  const [copyingLastWeek, setCopyingLastWeek] = useState(false);
  const [lastRememberedLine, setLastRememberedLine] = useState(() => readLastWeeklyLine());
  const [reviewing, setReviewing] = useState(null);
  const [saveState, setSaveState] = useState({ status: 'idle', message: '' });

  const weekDates = useMemo(() => weekForDate(weekAnchor, 0), [weekAnchor]);
  const selectedCrew = crews.find((crew) => String(crew.id) === String(crewId)) || null;
  const weekHours = linesWeekTotal(weeklyLines);
  const weekAllowances = weekDates.reduce((sum, day) => (
    sum + allowanceTotal(dailyAllowances[day.dateStr])
  ), 0);
  const crewHours = crewRows.reduce((sum, row) => sum + dayTotal(row), 0);
  const ownWeek = isSameEmployee(employee, selfEmployee);
  const filledLine = lastFilledWeeklyLine(weeklyLines);
  const lastJobLabel = formatJobCodeLabel(filledLine || lastRememberedLine);
  const canUseLastLine = Boolean(!filledLine && lastRememberedLine && lineHasJobAndCode(lastRememberedLine));
  const canRepeatLine = Boolean(filledLine);

  const rememberLine = (line) => {
    const snapshot = rememberLastWeeklyLine(line);
    if (snapshot) setLastRememberedLine(snapshot);
  };

  const loadWeekEntries = async (weekStart, empCode) => {
    if (demo) return demoEntriesForWeek(weekStart, empCode);
    const response = await authenticatedApiCall(
      instance,
      accounts,
      'GET',
      `/actual-spent/week/${weekStart}?projectId=null&empCode=${encodeURIComponent(empCode)}`
    );
    if (response.data && Object.prototype.hasOwnProperty.call(response.data, 'canUseWeeklyTimesheet')) {
      setCanUseWeekly(response.data.canUseWeeklyTimesheet === true);
    }
    return normalizeList(response.data?.data ?? response.data);
  };
  const padChain = useMemo(() => {
    if (mode === 'weekly' && openDay) {
      const match = weeklyLines.flatMap((line) => weekDates.map((day) => ({
        line,
        day,
        key: `${line.id}:${day.dateStr}`,
      }))).find((item) => item.key === openDay);
      return match ? weeklyPadChain(match.line.id, match.day.dateStr) : [];
    }
    if (mode === 'daily') return dailyPadChain(crewRows);
    return [];
  }, [crewRows, mode, openDay, weekDates, weeklyLines]);

  const focusPad = (next) => {
    setPadFocus(next);
    if (next?.kind === 'crewAllow') setOpenAllowances(next.id);
  };

  const padValue = (focus) => {
    if (!focus) return '';
    if (focus.kind === 'weekHour') {
      const line = weeklyLines.find((item) => item.id === focus.id);
      return hoursForDate(line, focus.date)[focus.field] ?? '';
    }
    if (focus.kind === 'weekAllow') {
      return (dailyAllowances[focus.date] || emptyAllowances())[focus.field] ?? '';
    }
    if (focus.kind === 'crewHour' || focus.kind === 'crewAllow') {
      const row = crewRows.find((item) => item.id === focus.id);
      return row?.[focus.field] ?? '';
    }
    if (focus.kind === 'crewShare') return crewShareAllowances[focus.field] ?? '';
    return '';
  };

  const writePadValue = (focus, value) => {
    if (focus.kind === 'weekHour') {
      setWeeklyLines((current) => current.map((item) => (
        item.id === focus.id ? setLineDayHours(item, focus.date, focus.field, value) : item
      )));
      return;
    }
    if (focus.kind === 'weekAllow') {
      setDailyAllowances((current) => ({
        ...current,
        [focus.date]: {
          ...(current[focus.date] || emptyAllowances()),
          [focus.field]: value,
        },
      }));
      return;
    }
    if (focus.kind === 'crewHour' || focus.kind === 'crewAllow') {
      setCrewRows((current) => current.map((item) => (
        item.id === focus.id ? { ...item, [focus.field]: value } : item
      )));
      return;
    }
    if (focus.kind === 'crewShare') {
      setCrewShareAllowances((current) => ({ ...current, [focus.field]: value }));
    }
  };

  const onPadKey = (key) => {
    if (!padFocus) return;
    writePadValue(padFocus, appendNumeric(padValue(padFocus), key));
  };

  const onPadNext = () => {
    const next = nextPadFocus(padChain, padFocus);
    if (!next) {
      setPadFocus(null);
      return;
    }
    focusPad(next);
  };

  useEffect(() => {
    if (demo) return undefined;
    let active = true;
    const loadAccess = async () => {
      try {
        const response = await authenticatedApiCall(
          instance,
          accounts,
          'GET',
          `/actual-spent/week/${currentDateString}?projectId=null`
        );
        if (!active) return;
        const allowed = response.data?.canUseWeeklyTimesheet === true;
        setCanUseWeekly(allowed);
        if (allowed) setMode('weekly');
      } catch {
        if (active) setCanUseWeekly(false);
      }
    };
    loadAccess();
    return () => { active = false; };
  }, [accounts, demo, instance]);

  useEffect(() => {
    if (!canUseWeekly && mode === 'weekly') setMode('daily');
  }, [canUseWeekly, mode]);

  useEffect(() => {
    setPadFocus(null);
    setReviewing(null);
  }, [mode, employee, weekAnchor, crewDate, crewId]);

  useEffect(() => {
    if (!padFocus) return undefined;
    const timer = window.setTimeout(() => {
      document.querySelector('.lgl-m-hours-field input.lgl-m-padfield.is-on')?.scrollIntoView({
        block: 'center',
        behavior: 'smooth',
      });
    }, 50);
    return () => window.clearTimeout(timer);
  }, [padFocus]);

  useEffect(() => {
    if (!openDay) {
      setPadFocus((current) => (current?.kind === 'weekHour' || current?.kind === 'weekAllow' ? null : current));
    }
  }, [openDay]);

  const codesFor = (projectId) => (
    (projectId && codeCache[projectId]) || CATALOGUE_CODES
  );

  useEffect(() => {
    if (demo) return undefined;
    let active = true;
    const loadEmployee = async () => {
      const email = accounts?.[0]?.username;
      if (!email) {
        setLoading(false);
        return;
      }
      try {
        const response = await authenticatedApiCall(
          instance,
          accounts,
          'GET',
          `/lt-employee/by-email?email=${encodeURIComponent(email)}`
        );
        const emp = mapEmployee(response.data?.data);
        if (active && emp?.emp_code) {
          setSelfEmployee(emp);
          setEmployee(emp);
        }
      } catch {
        if (active) setEmployee(null);
      }
    };
    loadEmployee();
    return () => { active = false; };
  }, [accounts, demo, instance]);

  useEffect(() => {
    const last = lastFilledWeeklyLine(weeklyLines);
    if (last) rememberLine(last);
  }, [weeklyLines]);

  useEffect(() => {
    if (!employee?.emp_code || !weekDates[0]) return undefined;
    if (demo) {
      const rows = demoEntriesForWeek(weekDates[0].dateStr, employee.emp_code);
      const lines = groupWeekEntries(rows);
      setWeeklyLines(lines.length ? lines : [createWeeklyLine(projects[0] || null)]);
      setDailyAllowances(sumAllowancesByDate(rows));
      setOpenDay(null);
      setLoading(false);
      return undefined;
    }
    let active = true;
    const loadWeek = async () => {
      setLoading(true);
      try {
        const rows = await loadWeekEntries(weekDates[0].dateStr, employee.emp_code);
        if (!active) return;
        const lines = groupWeekEntries(rows);
        setWeeklyLines(lines.length ? lines : [createWeeklyLine(projects[0] || null)]);
        setDailyAllowances(sumAllowancesByDate(rows));
        setOpenDay(null);
      } catch {
        if (active) {
          setWeeklyLines([createWeeklyLine(projects[0] || null)]);
          setDailyAllowances({});
        }
      } finally {
        if (active) setLoading(false);
      }
    };
    loadWeek();
    return () => { active = false; };
  }, [accounts, demo, employee, instance, projects, weekDates]);

  useEffect(() => {
    if (demo) return undefined;
    let active = true;
    const loadCrews = async () => {
      try {
        const response = await authenticatedApiCall(instance, accounts, 'GET', '/crews/my-crews');
        const list = normalizeList(response.data?.data ?? response.data);
        if (!active) return;
        setCrews(list);
        if (list.length === 1) {
          setCrewId(String(list[0].id));
        }
      } catch {
        if (active) setCrews([]);
      }
    };
    loadCrews();
    return () => { active = false; };
  }, [accounts, demo, instance]);

  useEffect(() => {
    if (!selectedCrew) {
      setCrewRows([]);
      return;
    }
    const project = defaultCrewProject(selectedCrew)
      || projects.find((item) => String(item.id) === String(selectedCrew.projects?.[0]?.project_id))
      || projects[0]
      || null;
    setCrewRows(crewPeople(selectedCrew).map((person) => createCrewRow(person, {
      id: project?.project_id || project?.id,
      project_id: project?.project_id || project?.id,
      job_number: project?.job_number,
      discipline_code: inferDisciplineCode(project),
    })));
  }, [projects, selectedCrew]);

  useEffect(() => {
    if (demo) return undefined;
    const projectIds = [
      ...weeklyLines.map((line) => line.project_id),
      ...crewRows.map((row) => row.project_id),
    ].filter(Boolean);
    const missing = [...new Set(projectIds)].filter((id) => !codeCache[id]);
    if (!missing.length) return undefined;
    let active = true;
    const load = async () => {
      const next = {};
      await Promise.all(missing.map(async (projectId) => {
        try {
          const response = await authenticatedApiCall(
            instance,
            accounts,
            'GET',
            `/op-codes?jobId=${encodeURIComponent(projectId)}&activeOnly=true`
          );
          const rows = normalizeOpCodes(normalizeList(response.data?.data ?? response.data));
          next[projectId] = rows.length ? rows : CATALOGUE_CODES;
        } catch {
          next[projectId] = CATALOGUE_CODES;
        }
      }));
      if (active) setCodeCache((current) => ({ ...current, ...next }));
    };
    load();
    return () => { active = false; };
  }, [accounts, codeCache, crewRows, demo, instance, weeklyLines]);

  const selectedCodeFor = (code) => (
    CATALOGUE_CODES.find((item) => item.code === code)
    || Object.values(codeCache).flat().find((item) => item.code === code)
    || (code ? { code, description: '' } : null)
  );

  const setCrewJob = (projectId) => {
    const project = projects.find((item) => String(item.id) === String(projectId));
    setCrewRows((current) => applyJobAndCode(current, jobFields(project)));
  };

  const setCrewCode = (next) => {
    setCrewRows((current) => applyJobAndCode(current, {
      op_code: next?.code || '',
      op_code_description: next?.description || '',
    }));
  };

  const applyLastJobCode = () => {
    const last = filledLine || lastRememberedLine;
    if (!lineHasJobAndCode(last)) return;
    rememberLine(last);
    setWeeklyLines((current) => applyRepeatedLine(current, last));
    setOpenDay(null);
    setSaveState({ status: 'idle', message: '' });
  };

  const applyLastJobCodeToCrew = () => {
    const last = lastRememberedLine;
    if (!lineHasJobAndCode(last)) return;
    const project = projects.find((item) => String(item.id) === String(last.project_id));
    setCrewRows((current) => applyJobAndCode(current, jobFields(project || last, {
      op_code: last.op_code,
      op_code_description: last.op_code_description || '',
    })));
    setSaveState({ status: 'idle', message: '' });
  };

  const applyCopiedWeek = (rows) => {
    const lines = copyWeekJobCodes(rows);
    if (!lines.length) {
      setSaveState({ status: 'error', message: 'No entries found for last week.' });
      return;
    }
    setWeeklyLines(lines);
    setDailyAllowances({});
    setOpenDay(null);
    lines.forEach((line) => rememberLine(line));
    setSaveState({
      status: 'saved',
      message: `Copied ${lines.length} job / code ${lines.length === 1 ? 'row' : 'rows'} from last week — fill in the hours.`,
    });
  };

  const copyLastWeek = async () => {
    if (!employee?.emp_code || !weekDates[0] || copyingLastWeek) return;
    const prevWeekStart = shiftWeek(weekDates[0].dateStr, -7);
    if (demo) {
      applyCopiedWeek(demoEntriesForWeek(prevWeekStart, employee.emp_code));
      return;
    }
    setCopyingLastWeek(true);
    setSaveState({ status: 'saving', message: 'Copying last week…' });
    try {
      applyCopiedWeek(await loadWeekEntries(prevWeekStart, employee.emp_code));
    } catch {
      setSaveState({ status: 'error', message: 'Could not load last week.' });
    } finally {
      setCopyingLastWeek(false);
    }
  };

  const weeklyEntriesOrError = () => {
    const entries = buildWeekEntries(weeklyLines, weekDates, employee, dailyAllowances);
    if (!employee?.emp_code) {
      return { error: 'Your employee record is not linked yet. Use the website weekly sheet or ask Accounting.' };
    }
    if (!entries.length) {
      return { error: 'Add a job, operation code, and hours for at least one day.' };
    }
    const byDay = {};
    entries.forEach((entry) => {
      byDay[entry.date] = (byDay[entry.date] || 0) + hourValue(entry.reg) + hourValue(entry.t12) + hourValue(entry.dbl);
    });
    const over = Object.entries(byDay).find(([, hours]) => hours > 24);
    if (over) {
      return { error: `${over[0]} has ${over[1].toFixed(1)}h — the daily limit is 24.` };
    }
    return { entries };
  };

  const crewEntriesOrError = () => {
    if (!selectedCrew) {
      return { error: 'You are not a crew head. Open Crews on the website and name yourself the head.' };
    }
    const entries = buildCrewEntries(crewRows, crewDate);
    if (!entries.length) {
      return { error: 'Add a job, operation code, and hours for at least one person.' };
    }
    const over = overDailyLimit(dailyTotalsByEmployee(crewRows));
    if (over) {
      const [code, hours] = over;
      const name = crewRows.find((row) => row.emp_code === code)?.emp_name || code;
      return { error: `${name} has ${hours.toFixed(1)}h on ${crewDate} — the daily limit is 24.` };
    }
    return { entries };
  };

  const requestWeeklySave = () => {
    setPadFocus(null);
    const next = weeklyEntriesOrError();
    if (next.error) {
      setSaveState({ status: 'error', message: next.error });
      return;
    }
    setReviewing('weekly');
    setSaveState({ status: 'idle', message: '' });
  };

  const saveWeekly = async () => {
    setPadFocus(null);
    const next = weeklyEntriesOrError();
    if (next.error) {
      setReviewing(null);
      setSaveState({ status: 'error', message: next.error });
      return;
    }

    setSaveState({ status: 'saving', message: 'Saving this week…' });
    try {
      if (!demo) {
        const response = await authenticatedApiCall(instance, accounts, 'POST', '/actual-spent/week-entry', { entries: next.entries });
        if (response.data?.success === false) {
          throw new Error(response.data.message || 'Could not save the week');
        }
      }
      setReviewing(null);
      setSaveState({
        status: 'saved',
        message: demo ? 'Week recorded on this phone (preview).' : 'Week saved.',
      });
    } catch (err) {
      setSaveState({ status: 'error', message: saveErrorMessage(err, 'Could not save the week') });
    }
  };

  const requestCrewSave = () => {
    setPadFocus(null);
    const next = crewEntriesOrError();
    if (next.error) {
      setSaveState({ status: 'error', message: next.error });
      return;
    }
    setReviewing('daily');
    setSaveState({ status: 'idle', message: '' });
  };

  const saveCrew = async () => {
    setPadFocus(null);
    const next = crewEntriesOrError();
    if (next.error) {
      setReviewing(null);
      setSaveState({ status: 'error', message: next.error });
      return;
    }

    setSaveState({ status: 'saving', message: 'Submitting crew hours…' });
    try {
      if (!demo) {
        const response = await authenticatedApiCall(
          instance,
          accounts,
          'POST',
          '/actual-spent/daily-crew-entry',
          { entries: next.entries }
        );
        if (response.data?.success === false) {
          throw new Error(response.data.message || 'Could not submit crew hours');
        }
      }
      setReviewing(null);
      setSaveState({
        status: 'saved',
        message: demo ? 'Crew hours recorded on this phone (preview).' : `Saved ${next.entries.length} crew ${next.entries.length === 1 ? 'entry' : 'entries'}.`,
      });
    } catch (err) {
      setSaveState({ status: 'error', message: saveErrorMessage(err, 'Could not submit crew hours') });
    }
  };

  const crewProjectId = crewRows[0]?.project_id || '';
  const crewCode = selectedCodeFor(crewRows[0]?.op_code);

  return (
    <>
      <header className="lgl-m-top">
        <div className="lgl-m-navrow">
          <Back onClick={onBack} />
          <div>
            <p className="lgl-m-kicker">Accounting</p>
            <h1 className="lgl-m-title">Time Tracker</h1>
            <p className="lgl-m-sub">
              {mode === 'weekly'
                ? (ownWeek || !employee?.emp_name
                  ? 'Your week, Sunday to Saturday.'
                  : `${employee.emp_name}'s week, Sunday to Saturday.`)
                : 'Foreman sheet: one day for the whole crew.'}
            </p>
          </div>
          <HeaderTools />
        </div>
        <div className="lgl-m-tabs lgl-m-tabs-2" role="tablist" aria-label="Time Tracker views">
          {canUseWeekly ? (
            <button type="button" role="tab" aria-selected={mode === 'weekly'} className={mode === 'weekly' ? 'is-on' : ''} onClick={() => { setMode('weekly'); setSaveState({ status: 'idle', message: '' }); }}>
              Weekly
            </button>
          ) : null}
          <button type="button" role="tab" aria-selected={mode === 'daily'} className={mode === 'daily' ? 'is-on' : ''} onClick={() => { setMode('daily'); setSaveState({ status: 'idle', message: '' }); }}>
            Daily crew
          </button>
        </div>
      </header>
      <main className={`lgl-m-body lgl-m-tt${padFocus ? ' has-numpad' : ''}`}>
        {mode === 'weekly' ? (
          <>
            {reviewing === 'weekly' ? null : (
            <>
            <EmployeePicker
              value={employee}
              selfEmployee={selfEmployee}
              demo={demo}
              instance={instance}
              accounts={accounts}
              onSelect={(next) => {
                setEmployee(next);
                setSaveState({ status: 'idle', message: '' });
              }}
            />
            {!ownWeek && selfEmployee ? (
              <button
                type="button"
                className="lgl-m-chipbtn"
                onClick={() => {
                  setEmployee(selfEmployee);
                  setSaveState({ status: 'idle', message: '' });
                }}
              >
                Back to my week
              </button>
            ) : null}
            <div className="lgl-m-weeknav">
              <button type="button" className="lgl-m-weekbtn" onClick={() => setWeekAnchor((current) => shiftWeek(current, -7))} aria-label="Previous week">←</button>
              <div>
                <p className="lgl-m-jobname">{formatWeekRange(weekDates)}</p>
                <p className="lgl-m-sub">
                  {weekHours.toFixed(1)}h
                  {weekAllowances ? ` · ${formatMoney(weekAllowances)} allowances` : ''}
                </p>
              </div>
              <button type="button" className="lgl-m-weekbtn" onClick={() => setWeekAnchor((current) => shiftWeek(current, 7))} aria-label="Next week">→</button>
            </div>
            <div className="lgl-m-week-actions">
              <button
                type="button"
                className="lgl-m-chipbtn"
                disabled={!employee?.emp_code || copyingLastWeek || loading}
                onClick={copyLastWeek}
              >
                {copyingLastWeek ? 'Copying last week…' : 'Copy last week'}
              </button>
              {canUseLastLine ? (
                <button type="button" className="lgl-m-chipbtn" onClick={applyLastJobCode}>
                  {`Use ${lastJobLabel} again`}
                </button>
              ) : null}
              {canRepeatLine ? (
                <button type="button" className="lgl-m-chipbtn" onClick={applyLastJobCode}>
                  {`Repeat ${lastJobLabel}`}
                </button>
              ) : null}
            </div>
            </>
            )}
            {loading ? <p className="lgl-m-empty">Loading this week…</p> : null}
            {reviewing === 'weekly' ? (
              <article className="lgl-m-card lgl-m-week-card lgl-m-review">
                <p className="lgl-m-kicker">Review this week</p>
                <p className="lgl-m-jobname">
                  {ownWeek || !employee?.emp_name ? 'Your week' : `${employee.emp_name}'s week`}
                  {` · ${formatWeekRange(weekDates)}`}
                </p>
                <ReviewTable
                  label="Line"
                  {...weekReviewTable(weeklyLines, weekDates, dailyAllowances)}
                />
                <p className="lgl-m-total">{weekHours.toFixed(1)}h this week</p>
              </article>
            ) : weeklyLines.map((line, lineIndex) => {
              const code = selectedCodeFor(line.op_code);
              const openDayMatch = weekDates.find((day) => openDay === `${line.id}:${day.dateStr}`) || null;
              const openHours = openDayMatch ? hoursForDate(line, openDayMatch.dateStr) : emptyDayHours();
              const openAllowances = openDayMatch
                ? (dailyAllowances[openDayMatch.dateStr] || emptyAllowances())
                : emptyAllowances();
              const openMoney = formatMoney(allowanceTotal(openAllowances));
              return (
                <article className="lgl-m-card lgl-m-week-card" key={line.id}>
                  <JobSelect
                    projects={projects}
                    value={line.project_id}
                    onChange={(projectId) => {
                      const project = projects.find((item) => String(item.id) === String(projectId));
                      setWeeklyLines((current) => current.map((item, index) => (
                        index === lineIndex ? { ...item, ...jobFields(project) } : item
                      )));
                    }}
                  />
                  <CodePicker
                    codes={codesFor(line.project_id)}
                    value={code}
                    onSelect={(next) => setWeeklyLines((current) => current.map((item, index) => (
                      index === lineIndex
                        ? { ...item, op_code: next?.code || '', op_code_description: next?.description || '' }
                        : item
                    )))}
                  />
                  <div className="lgl-m-weekstrip" role="group" aria-label="Weekdays">
                    {weekDates.map((day) => {
                      const hours = hoursForDate(line, day.dateStr);
                      const total = dayTotal(hours);
                      const money = formatMoney(allowanceTotal(dailyAllowances[day.dateStr] || emptyAllowances()));
                      const open = openDay === `${line.id}:${day.dateStr}`;
                      return (
                        <button
                          key={day.dateStr}
                          type="button"
                          aria-pressed={open}
                          aria-label={weekCellLabel(day, total, money)}
                          className={`lgl-m-weekcell${total ? ' has-hours' : ''}${open ? ' is-on' : ''}`}
                          onClick={() => setOpenDay(open ? null : `${line.id}:${day.dateStr}`)}
                        >
                          <span>{DAY_LABELS[day.dayOfWeek]}</span>
                          <em>{day.dayNum}</em>
                          <strong>{total ? total.toFixed(1) : '—'}</strong>
                        </button>
                      );
                    })}
                  </div>
                  {openDayMatch ? (
                    <div className="lgl-m-dayedit">
                      <p className="lgl-m-kicker">
                        {DAY_LABELS[openDayMatch.dayOfWeek]} {openDayMatch.dayNum}
                        {` · ${formatDayHoursLabel(dayTotal(openHours), openMoney)}`}
                      </p>
                      <HourInputs
                        idPrefix={`${line.id}-${openDayMatch.dateStr}`}
                        hours={openHours}
                        padFocus={padFocus}
                        onPadFocus={focusPad}
                        padFor={(field) => ({ kind: 'weekHour', id: line.id, date: openDayMatch.dateStr, field })}
                        onChange={(field, value) => setWeeklyLines((current) => current.map((item, index) => (
                          index === lineIndex ? setLineDayHours(item, openDayMatch.dateStr, field, value) : item
                        )))}
                        onFillRegular={() => setWeeklyLines((current) => current.map((item, index) => (
                          index === lineIndex ? setLineDayHours(item, openDayMatch.dateStr, 'reg', '8') : item
                        )))}
                      />
                      <AllowanceInputs
                        idPrefix={`${line.id}-${openDayMatch.dateStr}-allow`}
                        value={openAllowances}
                        padFocus={padFocus}
                        onPadFocus={focusPad}
                        padFor={(field) => ({ kind: 'weekAllow', id: line.id, date: openDayMatch.dateStr, field })}
                        onChange={(field, value) => setDailyAllowances((current) => ({
                          ...current,
                          [openDayMatch.dateStr]: {
                            ...(current[openDayMatch.dateStr] || emptyAllowances()),
                            [field]: value,
                          },
                        }))}
                      />
                      <section className="lgl-m-field">
                        <label htmlFor={`${line.id}-${openDayMatch.dateStr}-note`}>Note</label>
                        <input
                          id={`${line.id}-${openDayMatch.dateStr}-note`}
                          type="text"
                          maxLength={160}
                          placeholder="Optional comment for this day"
                          value={openHours.comment || ''}
                          onChange={(event) => setWeeklyLines((current) => current.map((item, index) => (
                            index === lineIndex ? setLineDayHours(item, openDayMatch.dateStr, 'comment', event.target.value) : item
                          )))}
                        />
                      </section>
                    </div>
                  ) : null}
                  <p className="lgl-m-total">{weekTotal(line.days).toFixed(1)}h this line</p>
                  {weeklyLines.length > 1 ? (
                    <button
                      type="button"
                      className="lgl-m-linkbtn"
                      onClick={() => setWeeklyLines((current) => current.filter((_, index) => index !== lineIndex))}
                    >
                      Remove this job / code
                    </button>
                  ) : null}
                </article>
              );
            })}
            {reviewing === 'weekly' ? null : (
              <button
                type="button"
                className="lgl-m-linkbtn"
                onClick={() => setWeeklyLines((current) => [...current, createWeeklyLine(projects[0] || null)])}
              >
                Add another job / code
              </button>
            )}
            <div className="lgl-m-dock">
              {padFocus || reviewing !== 'weekly' ? null : (
                <p className="lgl-m-docksum">
                  <strong>{weekHours.toFixed(1)}h</strong>
                  {weekAllowances ? <span>{formatMoney(weekAllowances)} allowances</span> : <span>No allowances yet</span>}
                </p>
              )}
              <p className={`lgl-m-status${saveState.status === 'saved' ? ' is-ok' : ''}${saveState.status === 'error' ? ' is-err' : ''}`}>
                {saveState.message}
              </p>
              {padFocus ? (
                <NumberPad onKey={onPadKey} onNext={onPadNext} onDone={() => setPadFocus(null)} />
              ) : null}
              {reviewing === 'weekly' ? (
                <>
                  <button type="button" className="lgl-m-linkbtn" onClick={() => setReviewing(null)}>
                    Back to edit
                  </button>
                  <button type="button" className="lgl-m-btn" disabled={saveState.status === 'saving'} onClick={saveWeekly}>
                    {saveState.status === 'saving' ? 'Saving…' : 'Confirm save'}
                  </button>
                </>
              ) : (
                <button type="button" className="lgl-m-btn" disabled={saveState.status === 'saving'} onClick={requestWeeklySave}>
                  Save week
                </button>
              )}
            </div>
          </>
        ) : null}

        {mode === 'daily' ? (
          <>
            {!crews.length ? (
              <p className="lgl-m-empty">
                Daily crew is for crew heads. You are not assigned as a head yet. Open Crews on the website and name yourself the head.
              </p>
            ) : (
              <>
                {reviewing === 'daily' ? (
                  <article className="lgl-m-card lgl-m-week-card lgl-m-review">
                    <p className="lgl-m-kicker">Review crew hours</p>
                    <p className="lgl-m-jobname">
                      {(selectedCrew?.crew_name || selectedCrew?.name || 'Crew')} · {crewDate}
                    </p>
                    <p className="lgl-m-sub">
                      {crewRows[0]?.job_number || 'Job'} · {crewRows[0]?.op_code || 'No code'}
                    </p>
                    <ReviewTable
                      label="Name"
                      dayHeads={CREW_REVIEW_HEADS}
                      rows={crewReviewTable(crewRows)}
                    />
                    <p className="lgl-m-total">{crewHours.toFixed(1)}h this crew</p>
                  </article>
                ) : (
                <>
                <MobileSelect
                  id="time-crew"
                  label="Crew"
                  placeholder="Select a crew"
                  value={crewId}
                  options={crews.map((crew) => ({
                    value: crew.id,
                    label: crew.crew_name || crew.name || `Crew ${crew.id}`,
                  }))}
                  onChange={setCrewId}
                />
                <section className="lgl-m-field">
                  <label htmlFor="time-crew-date">Date</label>
                  <input id="time-crew-date" type="date" value={crewDate} onChange={(event) => setCrewDate(event.target.value)} />
                </section>
                <article className="lgl-m-card lgl-m-week-card">
                  <p className="lgl-m-kicker">Same job and code for everyone</p>
                  <JobSelect projects={projects} value={crewProjectId} onChange={setCrewJob} />
                  <CodePicker
                    codes={codesFor(crewProjectId)}
                    value={crewCode}
                    onSelect={setCrewCode}
                  />
                  {lastRememberedLine && !crewRows[0]?.op_code ? (
                    <button type="button" className="lgl-m-chipbtn" onClick={applyLastJobCodeToCrew}>
                      {`Use ${formatJobCodeLabel(lastRememberedLine)} again`}
                    </button>
                  ) : null}
                  <button
                    type="button"
                    className="lgl-m-chipbtn"
                    onClick={() => setCrewRows((current) => applyRegularHours(current, '8'))}
                  >
                    8h regular for everyone
                  </button>
                  <p className="lgl-m-kicker" style={{ marginTop: 14 }}>Allowances for everyone</p>
                  <AllowanceInputs
                    idPrefix="crew-share"
                    value={crewShareAllowances}
                    padFocus={padFocus}
                    onPadFocus={focusPad}
                    padFor={(field) => ({ kind: 'crewShare', field })}
                    onChange={(field, value) => setCrewShareAllowances((current) => ({
                      ...current,
                      [field]: value,
                    }))}
                  />
                  <button
                    type="button"
                    className="lgl-m-chipbtn"
                    onClick={() => setCrewRows((current) => applyAllowances(current, crewShareAllowances))}
                  >
                    Allowances for everyone
                  </button>
                </article>
                {crewRows.map((row, rowIndex) => (
                  <article className="lgl-m-card lgl-m-week-card" key={row.id}>
                    <div className="lgl-m-linehead">
                      <div>
                        <div className="lgl-m-jobname">{row.emp_name || row.emp_code}</div>
                        <div className="lgl-m-sub">{row.emp_code}</div>
                      </div>
                      <strong>{dayTotal(row).toFixed(1)}h</strong>
                    </div>
                    <HourInputs
                      idPrefix={row.id}
                      hours={row}
                      padFocus={padFocus}
                      onPadFocus={focusPad}
                      padFor={(field) => ({ kind: 'crewHour', id: row.id, field })}
                      onChange={(field, value) => setCrewRows((current) => current.map((item, index) => (
                        index === rowIndex ? { ...item, [field]: value } : item
                      )))}
                      onFillRegular={() => setCrewRows((current) => current.map((item, index) => (
                        index === rowIndex ? { ...item, reg: '8' } : item
                      )))}
                    />
                    <button
                      type="button"
                      className={`lgl-m-chipbtn${allowanceTotal(row) ? ' has-value' : ''}`}
                      onClick={() => setOpenAllowances((current) => (current === row.id ? null : row.id))}
                    >
                      {allowanceTotal(row)
                        ? `Allowances · ${formatMoney(allowanceTotal(row))}`
                        : 'Allowances'}
                    </button>
                    {openAllowances === row.id ? (
                      <AllowanceInputs
                        idPrefix={`${row.id}-allow`}
                        value={row}
                        padFocus={padFocus}
                        onPadFocus={focusPad}
                        padFor={(field) => ({ kind: 'crewAllow', id: row.id, field })}
                        onChange={(field, value) => setCrewRows((current) => current.map((item, index) => (
                          index === rowIndex ? { ...item, [field]: value } : item
                        )))}
                      />
                    ) : null}
                  </article>
                ))}
                </>
                )}
                <div className="lgl-m-dock">
                  {padFocus ? null : (
                    <p className="lgl-m-docksum">
                      <strong>{crewHours.toFixed(1)}h</strong>
                      <span>this crew</span>
                    </p>
                  )}
                  <p className={`lgl-m-status${saveState.status === 'saved' ? ' is-ok' : ''}${saveState.status === 'error' ? ' is-err' : ''}`}>
                    {saveState.message}
                  </p>
                  {padFocus ? (
                    <NumberPad onKey={onPadKey} onNext={onPadNext} onDone={() => setPadFocus(null)} />
                  ) : null}
                  {reviewing === 'daily' ? (
                    <>
                      <button type="button" className="lgl-m-linkbtn" onClick={() => setReviewing(null)}>
                        Back to edit
                      </button>
                      <button type="button" className="lgl-m-btn" disabled={saveState.status === 'saving'} onClick={saveCrew}>
                        {saveState.status === 'saving' ? 'Submitting…' : 'Confirm submit'}
                      </button>
                    </>
                  ) : (
                    <button type="button" className="lgl-m-btn" disabled={saveState.status === 'saving'} onClick={requestCrewSave}>
                      Submit crew hours
                    </button>
                  )}
                </div>
              </>
            )}
          </>
        ) : null}
      </main>
    </>
  );
};
